import { Skeleton } from 'primereact/skeleton';
import { useCallback, useMemo } from 'react';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import { Tooltip } from 'primereact/tooltip';
import { useSetAtom, useAtomValue } from 'jotai';
import {
  selectedTickerFilterAtom,
  selectWidgetAIAtom,
  selectedSectorFilterAtom,
} from '~/atoms/dashboardAtom';
import { NoDataAvailableTemplate } from '../../common/NoDataAvailableTemplate';
import { WidgetHeader } from '../../common/WidgetHeader';
import { useWidgetPopout } from '../../../../hooks/useWidgetPopout';

interface TickerWithCompany {
  ticker: string;
  companyName: string | null;
  hasAllocation?: boolean;
}

interface NewsImpact {
  title: string;
  affectedTickers: TickerWithCompany[];
  impact: string;
  isPositive: boolean;
  sector: string;
}

export type NewsImpactAnalysisWidgetProps = {
  newsImpacts: NewsImpact[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId: string;
  title?: string;
  timestamp?: string;
  snapshotId?: string | null;
};

export const NewsImpactAnalysisWidget: React.FC<
  NewsImpactAnalysisWidgetProps
> = ({
  newsImpacts,
  isLoading,
  error,
  refetch,
  handleRemoveWidget,
  widgetId,
  title = 'News Impact Analysis',
  timestamp,
  snapshotId,
}) => {
  const removeWidget = useCallback(
    () => handleRemoveWidget?.(widgetId),
    [widgetId, handleRemoveWidget],
  );
  const setSelectWidgetAI = useSetAtom(selectWidgetAIAtom);
  const handlePopout = useWidgetPopout({
    widgetId,
    widgetComponent: NewsImpactAnalysisWidget,
    title: title || 'News Impact Analysis Widget',
    defaultWidth: 1000,
    defaultHeight: 800,
    widgetProps: {
      newsImpacts,
      isLoading,
      error,
      refetch,
      handleRemoveWidget,
      widgetId,
      title,
      timestamp,
      snapshotId,
    },
  });

  const setSelectedTickerFilter = useSetAtom(selectedTickerFilterAtom);
  const selectedSectorFilter = useAtomValue(selectedSectorFilterAtom);

  const noData = useMemo(() => {
    return !newsImpacts.length;
  }, [newsImpacts]);

  if (isLoading) {
    return (
      <div className="p-3 h-full">
        <Skeleton className="h-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="Something went wrong."
          isError
        />
      </div>
    );
  }

  if (!snapshotId) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="No Snapshot Available."
          subTitle="Run portfolio analysis to generate snapshot"
        />
      </div>
    );
  }

  if (noData) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          title="No News Impact Data Available."
          subTitle="Run portfolio analysis to generate news impact analysis"
          icon="pi pi-newspaper"
        />
      </div>
    );
  }

  return (
    <div className="hover-container h-full flex flex-column" id={widgetId}>
      <WidgetHeader
        title={title}
        removeWidget={removeWidget}
        refetch={() => refetch()}
        setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
        onPopout={handlePopout}
        widgetId={widgetId}
        children={
          timestamp && (
            <span className="ml-2 bq-timestamp-badge">{timestamp}</span>
          )
        }
      />

      {/* Full Height Content with Scrollable Area */}
      <div className="flex-1 px-3 pt-3 overflow-y-auto">
        <Tooltip target=".ticker-badge" position="top" />
        <div
          className="flex flex-column gap-3"
          style={{ minHeight: '100%', paddingBottom: '1rem' }}
        >
          {newsImpacts.map((news, index) => (
            <Card
              key={`news-${index}`}
              className="flex flex-column"
              pt={{
                body: {
                  className: 'p-3 flex-1',
                },
                content: {
                  className: 'p-0 m-0 flex-1',
                },
              }}
              style={{
                borderLeft: '4px solid var(--bq-color-info)',
              }}
            >
              <div className="flex justify-content-between align-items-start mb-2">
                <div className="flex-1">
                  <div
                    className="text-sm mb-2"
                    style={{ color: 'var(--bq-text-primary)' }}
                  >
                    {news.title}
                  </div>
                  <div className="flex flex-wrap gap-2 align-items-center">
                    <span
                      className="text-xs"
                      style={{ color: 'var(--bq-text-secondary)' }}
                    >
                      Affects
                    </span>
                    {news.affectedTickers?.map((tickerData) => (
                      <div
                        key={tickerData.ticker}
                        style={{ position: 'relative' }}
                      >
                        <Badge
                          id={`ticker-${tickerData.ticker}`}
                          value={tickerData.ticker}
                          severity="info"
                          className={`text-xs cursor-pointer ticker-badge ${
                            tickerData.hasAllocation
                              ? 'bq-ticker-badge'
                              : 'bq-ticker-badge-outline'
                          }`}
                          title={tickerData.companyName || tickerData.ticker}
                          data-pr-tooltip={
                            tickerData.companyName || tickerData.ticker
                          }
                          onClick={() => {
                            setSelectedTickerFilter(tickerData.ticker);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="ml-3 flex flex-column align-items-end">
                  <div
                    className="text-xs mb-1"
                    style={{ color: 'var(--bq-text-secondary)' }}
                  >
                    {news.sector}
                  </div>
                  <span
                    className="font-semibold"
                    style={{
                      color: news.isPositive
                        ? 'var(--bq-color-sucess)'
                        : 'var(--bq-color-error)',
                    }}
                  >
                    {news.impact}
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};
